<script setup lang="ts">
import { onMounted, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import { useColorMode } from '@vueuse/core';
import {
  LayoutDashboard,
  Phone,
  Moon,
  Sun,
  SunMoon,
  FileText,
  Shield,
  TrendingUp,
  Users,
} from 'lucide-vue-next';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';

const router = useRouter();
const open = defineModel<boolean>('open', { default: false });
const mode = useColorMode();

// Watch for keyboard shortcut
onMounted(() => {
  const handleKeydown = (e: KeyboardEvent) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
      e.preventDefault();
      open.value = !open.value;
    }
  };

  document.addEventListener('keydown', handleKeydown);

  onUnmounted(() => {
    document.removeEventListener('keydown', handleKeydown);
  });
});

// Navigation helper
function navigateTo(path: string) {
  router.push(path);
  open.value = false;
}

// Theme helpers
function setTheme(newMode: 'light' | 'dark' | 'auto') {
  mode.value = newMode;
  open.value = false;
}

// External link helper
function openExternalLink(url: string) {
  window.open(url, '_blank', 'noopener,noreferrer');
  open.value = false;
}
</script>

<template>
  <CommandDialog v-model:open="open">
    <CommandInput placeholder="Type a command or search..." />
    <CommandList>
      <CommandEmpty>No results found.</CommandEmpty>

      <!-- Section 1: Pages -->
      <CommandGroup heading="Pages">
        <CommandItem value="dashboard" @select="navigateTo('/dashboard')">
          <LayoutDashboard class="mr-2 h-4 w-4" />
          <span>Dashboard</span>
        </CommandItem>
        <CommandItem value="calls" @select="navigateTo('/calls')">
          <Phone class="mr-2 h-4 w-4" />
          <span>Calls</span>
        </CommandItem>
        <CommandItem value="trends" @select="navigateTo('/dashboard?tab=trends')">
          <TrendingUp class="mr-2 h-4 w-4" />
          <span>Trends</span>
        </CommandItem>
        <CommandItem value="staff" @select="navigateTo('/dashboard?tab=staff-performance')">
          <Users class="mr-2 h-4 w-4" />
          <span>Staff</span>
        </CommandItem>
      </CommandGroup>

      <CommandSeparator />

      <!-- Section 2: Theme -->
      <CommandGroup heading="Theme">
        <CommandItem v-if="mode !== 'light'" value="light-mode" @select="setTheme('light')">
          <Sun class="mr-2 h-4 w-4" />
          <span>Switch to light mode</span>
        </CommandItem>
        <CommandItem v-if="mode !== 'dark'" value="dark-mode" @select="setTheme('dark')">
          <Moon class="mr-2 h-4 w-4" />
          <span>Switch to dark mode</span>
        </CommandItem>
        <CommandItem v-if="mode !== 'auto'" value="system-mode" @select="setTheme('auto')">
          <SunMoon class="mr-2 h-4 w-4" />
          <span>Follow system</span>
        </CommandItem>
      </CommandGroup>

      <CommandSeparator />

      <!-- Section 3: Legal -->
      <CommandGroup heading="Legal">
        <CommandItem value="terms" @select="openExternalLink('https://retaintive.ai/terms')">
          <FileText class="mr-2 h-4 w-4" />
          <span>Terms of Service</span>
        </CommandItem>
        <CommandItem value="privacy" @select="openExternalLink('https://retaintive.ai/privacy')">
          <Shield class="mr-2 h-4 w-4" />
          <span>Privacy Policy</span>
        </CommandItem>
      </CommandGroup>
    </CommandList>
  </CommandDialog>
</template>
